import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Loader2,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Button, Input } from '@tark/shared/components';
import { useAuth, useWebOnboarding } from '@tark/shared/hooks';
import {
  getErrorMessage,
  isEmailAlreadyExistsError,
  savePendingAccountRecovery,
} from '@tark/shared/lib';
import type { CompleteWebOnboardingPayload } from '@tark/shared/types';
import AuthShell from '@/components/auth/AuthShell';
import OtpResend from '@/components/auth/OtpResend';
import { useOtpRecovery } from '@/hooks/useOtpRecovery';
import { getWebDeviceMetadata } from '@/lib/webDevice';

type AuthStep = 'eligibility' | 'phone' | 'otp';
type SportSelection = CompleteWebOnboardingPayload['sport'];
type RegisterIntent = 'play' | 'pro' | 'join-org';

const REGISTER_STEPS = ['Eligibility', 'Phone', 'Verify', 'Legal', 'Record', 'Profile'];

function parseRegisterIntent(value: string | null): RegisterIntent {
  if (value === 'pro' || value === 'join-org') {
    return value;
  }
  return 'play';
}

function defaultReturnTarget(intent: RegisterIntent): string {
  switch (intent) {
    case 'pro':
      return '/pros/become';
    case 'join-org':
      return '/join-organization';
    case 'play':
    default:
      return '/welcome';
  }
}

function sanitizeReturnTarget(returnTarget: string | null, intent: RegisterIntent) {
  const fallback = defaultReturnTarget(intent);
  if (!returnTarget || !returnTarget.startsWith('/')) {
    return fallback;
  }
  return returnTarget;
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidUsPhoneNumber(value: string) {
  const digits = value.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

function FlowProgress({
  current,
  steps,
}: {
  current: number;
  steps: string[];
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-3 text-xs font-medium text-text-muted">
        <span>Step {current} of {steps.length}</span>
        <span>{steps[Math.max(0, Math.min(current - 1, steps.length - 1))]}</span>
      </div>

      <div className="mt-3 grid grid-cols-6 gap-2">
        {steps.map((step, index) => (
          <div
            key={step}
            className={`h-1.5 rounded-full transition-colors ${
              index < current ? 'bg-primary' : 'bg-border'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  );
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const intent = useMemo(() => parseRegisterIntent(searchParams.get('intent')), [searchParams]);
  const {
    requestOtp,
    verifyOtp,
    isRequestingOtp,
    isVerifyingOtp,
    isAuthenticated,
    isLoading,
    user,
    refetch,
    logout,
    isLoggingOut,
  } = useAuth();
  const {
    acceptLegalConsent,
    isAcceptingLegalConsent,
    attestAge,
    completeOnboarding,
    isAttestingAge,
    isCompletingOnboarding,
  } = useWebOnboarding();

  const [authStep, setAuthStep] = useState<AuthStep>('eligibility');
  const [adultIntentConfirmed, setAdultIntentConfirmed] = useState(false);
  const [under18Selected, setUnder18Selected] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const recovery = useOtpRecovery();
  const { otp, setOtp, sessionToken, error, setError } = recovery;
  const otpInput = useRef<HTMLInputElement>(null);
  useEffect(() => { if (sessionToken) otpInput.current?.focus(); }, [sessionToken]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedSmsConsent, setAcceptedSmsConsent] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [sport, setSport] = useState<SportSelection>('tennis');
  const [formError, setFormError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [zipTouched, setZipTouched] = useState(false);
  const [accountConflict, setAccountConflict] = useState(false);

  const returnTarget = useMemo(
    () => sanitizeReturnTarget(searchParams.get('return'), intent),
    [intent, searchParams]
  );

  const intentCopy = useMemo(() => {
    switch (intent) {
      case 'pro':
        return {
          chip: 'Provider path',
          title: 'Create your TARK account',
          description: 'First create your TARK account. After that you can request coach, stringer, or academy roles.',
          finishLabel: 'Finish creating account',
          nextNote: 'Next step: request the provider roles you want on this same TARK account.',
        };
      case 'join-org':
        return {
          chip: 'Join organization',
          title: 'Create your TARK account',
          description: 'First create your TARK account. After that you can join your organization.',
          finishLabel: 'Finish creating account',
          nextNote: 'Next step: use your invite or join code to connect this account to an organization.',
        };
      case 'play':
      default:
        return {
          chip: '',
          title: 'Create your TARK account',
          description: 'Create your TARK account to play with local squads.',
          finishLabel: 'Finish creating account',
          nextNote: '',
        };
    }
  }, [intent]);

  const progressStep = useMemo(() => {
    if (!isAuthenticated) {
      if (authStep === 'eligibility') return 1;
      return authStep === 'phone' ? 2 : 3;
    }
    if (user?.nextStep === 'LEGAL_CONSENT') {
      return 4;
    }
    if (user?.nextStep === 'AGE_VERIFICATION') {
      return 5;
    }
    if (user?.nextStep === 'MINOR_RESTRICTED') return 5;
    return 6;
  }, [authStep, isAuthenticated, user?.nextStep]);
  const isPhoneNumberValid = useMemo(() => isValidUsPhoneNumber(phoneNumber), [phoneNumber]);

  const emailError = emailTouched && email.trim() && !isValidEmail(email)
    ? 'Enter a valid email address.'
    : '';
  const zipError = zipTouched && zipCode && !/^\d{5}$/.test(zipCode)
    ? 'Enter a valid 5-digit ZIP code.'
    : '';

  useEffect(() => {
    try {
      window.localStorage.removeItem('tark_last_phone');
    } catch {
      // Ignore storage failures.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (user?.nextStep === 'TABS' || user?.nextStep === 'WELCOME') {
      navigate(returnTarget, { replace: true });
    }
  }, [isAuthenticated, navigate, returnTarget, user?.nextStep]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (!firstName && user.firstName) {
      setFirstName(user.firstName);
    }
    if (!lastName && user.lastName) {
      setLastName(user.lastName);
    }
    if (!email && user.email) {
      setEmail(user.email);
    }
  }, [email, firstName, lastName, user]);

  const handleRequestOtp = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setError('');

    if (!isPhoneNumberValid) {
      setError('Enter a valid mobile number.');
      return;
    }

    const response = await recovery.requestCode(() => requestOtp({ phoneNumber }));
    if (response) setAuthStep('otp');
  };

  const handleVerifyOtp = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    try {
      const response = await recovery.verifyCode(() => verifyOtp({ phoneNumber, otp, sessionToken }));
      if (!response) return;
      if (adultIntentConfirmed) {
        await attestAge('ADULT_18_PLUS');
        const updated = await refetch();
        if (updated.data?.nextStep === 'TABS' || updated.data?.nextStep === 'WELCOME') {
          navigate(returnTarget, { replace: true });
        }
      } else if (response.user.nextStep === 'TABS' || response.user.nextStep === 'WELCOME') {
        navigate(returnTarget, { replace: true });
      }
    } catch (error) {
      setError(getErrorMessage(error) || 'That code did not match. Try again or request a fresh one.');
    }
  };

  const handleAcceptLegalConsent = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError('');

    if (!acceptedTerms || !acceptedPrivacy || !acceptedSmsConsent) {
      setFormError('Please review and accept the Terms, Privacy Policy, and transactional SMS consent to continue.');
      return;
    }

    try {
      await acceptLegalConsent();
      await refetch();
    } catch {
      setFormError('We could not save your legal confirmations right now. Please try again.');
    }
  };

  const handleAgeAttestation = async (selection: 'ADULT_18_PLUS' | 'UNDER_18') => {
    setFormError('');

    try {
      await attestAge(selection);
      await refetch();
    } catch {
      setFormError('We could not save this selection right now. Please try again.');
    }
  };

  const handleRestrictedSignOut = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleExistingAccountRecovery = async () => {
    const replacementPhone = user?.phone || phoneNumber;
    if (!savePendingAccountRecovery(replacementPhone)) {
      setFormError('We could not prepare account recovery in this browser. Try again or contact support.');
      return;
    }

    await logout();
    const params = new URLSearchParams({ recovery: '1', return: returnTarget });
    navigate(`/login?${params.toString()}`, { replace: true });
  };

  const handleCompleteOnboarding = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError('');
    setEmailTouched(true);
    setZipTouched(true);

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setFormError('First name, last name, and email are required.');
      return;
    }

    if (!isValidEmail(email)) {
      setFormError('Enter a valid email address.');
      return;
    }

    if (!/^\d{5}$/.test(zipCode)) {
      setFormError('Enter a valid 5-digit ZIP code.');
      return;
    }

    const payload: CompleteWebOnboardingPayload = {
      ...getWebDeviceMetadata(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      emailSource: 'manual',
      zipCode,
      sport,
    };

    try {
      await completeOnboarding(payload);
      const updated = await refetch();
      if (updated.data?.nextStep === 'TABS' || updated.data?.nextStep === 'WELCOME') {
        navigate(returnTarget, { replace: true });
      }
    } catch (completeError) {
      if (isEmailAlreadyExistsError(completeError)) {
        setAccountConflict(true);
        setFormError('');
        return;
      }
      setFormError('We could not finish your setup right now. Please check the details and try again.');
    }
  };

  const renderRegistrationStep = () => {
    if (!isAuthenticated && authStep === 'eligibility') {
      return (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h2 className="mt-5 text-center text-xl font-semibold text-text">Who will use this account?</h2>
          <p className="mt-2 text-center text-sm leading-6 text-text-muted">
            TARK self-service accounts are for adults. Choose the option that applies to the person using this account.
          </p>

          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={() => {
                setAdultIntentConfirmed(true);
                setUnder18Selected(false);
                setAuthStep('phone');
              }}
              className="flex w-full items-center gap-4 rounded-lg border border-border bg-surface px-4 py-4 text-left transition-colors hover:border-primary"
            >
              <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
              <span className="flex-1">
                <span className="block font-semibold text-text">I am 18 or older</span>
                <span className="mt-1 block text-sm text-text-muted">Continue with an adult self-service account.</span>
              </span>
              <ArrowRight className="h-4 w-4 text-text-muted" />
            </button>

            <button
              type="button"
              onClick={() => setUnder18Selected(true)}
              className="flex w-full items-center gap-4 rounded-lg border border-border bg-surface px-4 py-4 text-left transition-colors hover:border-primary"
            >
              <Users className="h-5 w-5 shrink-0 text-primary" />
              <span className="flex-1">
                <span className="block font-semibold text-text">I am under 18</span>
                <span className="mt-1 block text-sm text-text-muted">Use a guardian-managed or organization player profile.</span>
              </span>
            </button>
          </div>

          {under18Selected ? (
            <div className="mt-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-4 text-sm leading-6 text-text-muted">
              No account has been created. Ask a parent, guardian, coach, or organization to manage your player profile. Minor profiles do not receive Community chat, discovery, direct messages, or wallet access.
            </div>
          ) : null}

          <p className="mt-5 text-center text-xs leading-5 text-text-muted">
            This is a self-attestation, not identity or age-document verification.
          </p>
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
          {authStep === 'phone' ? (
            <form onSubmit={handleRequestOtp} className="space-y-5">
              <Input
                label="Phone number"
                disabled={recovery.busy}
                type="tel"
                placeholder="(555) 123-4567"
                value={phoneNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(event.target.value)}
                leftIcon={<Phone className="h-4 w-4" />}
                error={error || undefined}
                autoFocus
                autoComplete="tel"
                size="lg"
                className="bg-surface text-text"
              />

              <Button
                type="submit"
                size="lg"
                className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
                disabled={!isPhoneNumberValid || recovery.busy || recovery.resendIn > 0}
                isLoading={isRequestingOtp}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                {recovery.resendIn > 0 ? `Send code in ${recovery.resendIn}s` : 'Send registration code'}
              </Button>

              <p className="text-xs leading-6 text-text-muted">
                By requesting a code, you agree to receive transactional account texts from TARK.
                Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help.
                Review our <a href="/terms" className="font-semibold text-primary">Terms</a>, <a href="/privacy" className="font-semibold text-primary">Privacy Policy</a>, and <a href="/sms-opt-in" className="font-semibold text-primary">SMS terms</a>.
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="rounded-lg border border-border bg-surface px-4 py-3 text-sm text-text-muted">
                We sent a 6-digit code to <span className="font-semibold text-text">{phoneNumber}</span>.
              </div>

              <Input
                label="Verification code"
                ref={otpInput}
                disabled={recovery.busy || recovery.expired}
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                error={error || undefined}
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                size="lg"
                className="bg-surface text-text"
              />

              <Button
                type="submit"
                size="lg"
                className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
                isLoading={isVerifyingOtp}
                disabled={otp.length !== 6 || recovery.busy || recovery.expired || recovery.verificationBlocked || !sessionToken}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Verify and continue
              </Button>

              <OtpResend seconds={recovery.resendIn} busy={recovery.busy}
                notice={recovery.notice} onResend={() => { void handleRequestOtp(); }} />

              <button
                type="button"
                disabled={recovery.busy}
                onClick={() => {
                  setAuthStep('phone');
                  recovery.clearChallenge();
                }}
                className="w-full text-sm font-medium text-text-muted transition-colors hover:text-text"
              >
                Use a different number
              </button>
            </form>
          )}
        </div>
      );
    }

    if (user?.nextStep === 'LEGAL_CONSENT') {
      return (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
          <form onSubmit={handleAcceptLegalConsent} className="space-y-4">
            <label className="flex items-start gap-3 rounded-lg border border-border bg-surface px-4 py-4 text-sm leading-6 text-text-muted">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span>
                I agree to the <a href="/terms" className="font-semibold text-primary">Terms of Service</a> for TARK, operated by ALAMPANA LLC d/b/a TARK.
              </span>
            </label>

            <label className="flex items-start gap-3 rounded-lg border border-border bg-surface px-4 py-4 text-sm leading-6 text-text-muted">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(event) => setAcceptedPrivacy(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span>
                I acknowledge the <a href="/privacy" className="font-semibold text-primary">Privacy Policy</a>, including how TARK uses my phone number, email, and account activity to operate the service.
              </span>
            </label>

            <label className="flex items-start gap-3 rounded-lg border border-border bg-surface px-4 py-4 text-sm leading-6 text-text-muted">
              <input
                type="checkbox"
                checked={acceptedSmsConsent}
                onChange={(event) => setAcceptedSmsConsent(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
              <span>
                I consent to receive transactional SMS verification codes from TARK for sign-in and account security. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out or HELP for help. See <a href="/sms-opt-in" className="font-semibold text-primary">SMS terms</a>.
              </span>
            </label>

            {formError ? <ErrorBanner message={formError} /> : null}

            <Button
              type="submit"
              size="lg"
              className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
              isLoading={isAcceptingLegalConsent}
              rightIcon={<ArrowRight className="h-4 w-4" />}
            >
              Continue
            </Button>
          </form>
        </div>
      );
    }

    if (user?.nextStep === 'MINOR_RESTRICTED') {
      return (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Users className="h-7 w-7" />
          </div>
          <h2 className="mt-5 text-center text-xl font-semibold text-text">Guardian-managed access required</h2>
          <p className="mt-3 text-center text-sm leading-6 text-text-muted">
            This account cannot use Community chat, discovery, direct messages, or wallet features. A guardian, coach, or organization can manage a separate player profile.
          </p>
          <div className="mt-6 space-y-3">
            <Button
              type="button"
              size="lg"
              className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
              isLoading={isLoggingOut}
              onClick={handleRestrictedSignOut}
            >
              Sign out
            </Button>
            <a
              href="/data-request"
              className="flex min-h-11 w-full items-center justify-center rounded-lg border border-border bg-surface px-4 text-sm font-semibold text-text hover:border-primary"
            >
              Request account deletion or correction
            </a>
            <a href="mailto:support@playtark.com?subject=Age%20eligibility%20help" className="block text-center text-sm font-semibold text-primary">
              Contact support
            </a>
          </div>
        </div>
      );
    }

    if (user?.nextStep === 'AGE_VERIFICATION') {
      return (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
          <div className="rounded-lg border border-border bg-surface px-4 py-4 text-sm leading-6 text-text-muted">
            <p className="font-semibold text-text">TARK self-service accounts are open to adults only.</p>
            <p className="mt-2">
              Choose the option that applies to the person using this account. Your selection is recorded as a self-attestation, not identity or age-document verification.
            </p>
          </div>

          {formError ? <div className="mt-5"><ErrorBanner message={formError} /></div> : null}

          <div className="mt-6 space-y-3">
            <Button
              type="button"
              size="lg"
              className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
              isLoading={isAttestingAge}
              rightIcon={<ArrowRight className="h-4 w-4" />}
              onClick={() => handleAgeAttestation('ADULT_18_PLUS')}
            >
              I am 18 or older
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full rounded-lg"
              disabled={isAttestingAge}
              onClick={() => handleAgeAttestation('UNDER_18')}
            >
              I am under 18
            </Button>
          </div>
        </div>
      );
    }

    if (accountConflict) {
      return (
        <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <h2 className="mt-5 text-center text-xl font-semibold text-text">Protect your existing account</h2>
          <p className="mt-3 text-center text-sm leading-6 text-text-muted">
            These details are already connected to a TARK account. Sign in to that account, then verify this phone number before replacing the old one.
          </p>
          <div className="mt-6 space-y-3">
            <Button
              type="button"
              size="lg"
              className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
              isLoading={isLoggingOut}
              rightIcon={<ArrowRight className="h-4 w-4" />}
              onClick={handleExistingAccountRecovery}
            >
              Sign in to existing account
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full rounded-lg"
              disabled={isLoggingOut}
              onClick={() => {
                setAccountConflict(false);
                setFormError('');
              }}
            >
              Use a different email
            </Button>
          </div>
          {formError ? <div className="mt-4"><ErrorBanner message={formError} /></div> : null}
          <p className="mt-5 text-center text-xs leading-5 text-text-muted">
            TARK will not combine accounts until both the existing account and the new phone number are verified.
          </p>
        </div>
      );
    }

    return (
      <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
        <p className="mb-5 text-sm text-text-muted">
          Phone: <span className="font-semibold text-text">{user?.phone || phoneNumber}</span>
        </p>

        {intentCopy.nextNote ? (
          <div className="mb-5 rounded-lg border border-primary/15 bg-primary/5 px-4 py-3 text-sm leading-6 text-text-muted">
            <span className="font-semibold text-text">This step creates your TARK account first.</span>{' '}
            {intentCopy.nextNote}
          </div>
        ) : null}

        <form onSubmit={handleCompleteOnboarding} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              label="First name"
              value={firstName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
              size="lg"
              className="bg-surface text-text"
            />
            <Input
              label="Last name"
              value={lastName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLastName(event.target.value)}
              size="lg"
              className="bg-surface text-text"
            />
          </div>

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
            onBlur={() => setEmailTouched(true)}
            leftIcon={<Mail className="h-4 w-4" />}
            error={emailError || undefined}
            size="lg"
            className="bg-surface text-text"
          />

          <Input
            label="ZIP code"
            inputMode="numeric"
            maxLength={5}
            value={zipCode}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setZipCode(event.target.value.replace(/\D/g, '').slice(0, 5))}
            onBlur={() => setZipTouched(true)}
            leftIcon={<MapPin className="h-4 w-4" />}
            hint="Used to show local squads, sessions, and providers."
            error={zipError || undefined}
            size="lg"
            className="bg-surface text-text"
          />

          <div>
            <label className="mb-2 block text-sm font-medium text-text">Primary sport</label>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { value: 'tennis', label: 'Tennis' },
                { value: 'pickleball', label: 'Pickleball' },
                { value: 'both', label: 'Both' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                    onClick={() => setSport(option.value as SportSelection)}
                    className={`rounded-lg border px-4 py-3 text-sm font-semibold transition-all ${
                      sport === option.value
                        ? 'border-2 border-primary bg-primary/12 font-bold text-primary shadow-[0_0_0_1px_rgba(0,135,90,0.08)]'
                        : 'border-border bg-surface text-text-muted hover:border-primary/45 hover:text-text'
                    }`}
                  >
                    {option.label}
                  </button>
              ))}
            </div>
          </div>

          {formError ? <ErrorBanner message={formError} /> : null}

          <Button
            type="submit"
            size="lg"
            className="w-full rounded-lg bg-text text-white hover:bg-text/90 active:bg-text/90"
            isLoading={isCompletingOnboarding}
            rightIcon={<CheckCircle2 className="h-4 w-4" />}
          >
            {intentCopy.finishLabel}
          </Button>
        </form>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // New accounts are created in the phone app. Keep this route for an already
  // authenticated account that still needs to finish its existing setup.
  if (!isAuthenticated) {
    return (
      <AuthShell
        eyebrow="Get started"
        title="Start in the TARK phone app"
        description="Create your TARK account in the phone app. Once your phone and email are verified, you can also sign in here."
        footer={<Link to={`/login?return=${encodeURIComponent(returnTarget)}`} className="font-semibold text-primary">Already have an account? Sign in</Link>}
      >
        <div className="rounded-2xl border border-border bg-white p-6 shadow-[var(--shadow-card)]">
          <p className="text-sm leading-6 text-text-muted">
            TARK web sign-in is for existing members and approved PROs. The app is available to our current testers; store links will appear here when the listings are live.
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={intentCopy.title}
      description={intentCopy.description}
      headerSlot={
        <div className="space-y-3">
          {intentCopy.chip ? (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/15 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {intentCopy.chip}
            </div>
          ) : null}
          <FlowProgress current={progressStep} steps={REGISTER_STEPS} />
        </div>
      }
      footer={
        <div className="flex flex-col gap-3 text-sm text-text-muted sm:flex-row sm:items-center sm:justify-between">
          <span>Already have an account?</span>
          <Link
            to={`/login?return=${encodeURIComponent(returnTarget)}`}
            className="inline-flex items-center gap-2 font-semibold text-primary"
          >
            Sign in instead
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      }
    >
      {renderRegistrationStep()}
    </AuthShell>
  );
}
